import * as React from 'react';
import { ButtonAttrs } from '../../utils/HtmlAttrs';
import { InteractionState } from '../../utils/InteractionState';

export enum ButtonType {
  Destructive = 0,
  /** @deprecated use `Destructive` */ Attention = 0,
  /** @deprecated use `Destructive` */ OutlineAttention = 6,
  Primary = 2,
  /** @deprecated use `Primary` */ Go = 2,
  /** @deprecated use `Primary` */ OutlineGo = 5,
  Outline = 3,
  /** @deprecated use `Outline` */ Grey = 1,
  OutlineDark = 4,
  /** @deprecated use `OutlineDark` */ LightOutline = 4,
  LowEmphasis = 9,
  /** @deprecated use `LowEmphasis` */ Text = 7,
  /** @deprecated use `LowEmphasis` */ DarkText = 8,
  /** @deprecated use `LowEmphasis` */ DarkBoldText = 9,
  /** @deprecated use `Outline` or `LowEmphasis` */ Icon = 10,
  /** @deprecated use `Outline` or `LowEmphasis` */ IconOutline = 11,
  LowEmphasisDark = 12,
}

export interface ButtonProps extends Pick<ButtonAttrs, 'onClick'> {
  /** A ref to the button element. */
  buttonRef?: React.Ref<HTMLButtonElement>;
  /**
   * The type of button style to use.
   * @default ```ButtonType.Outline```
   */
  buttonType?: ButtonType;
  /**
   * The contents of the button element.
   */
   children?: React.ReactNode;
  /**
   * Medium- and high-density buttons have less padding in order to fit into tighter spaces.
   * NOTE: on low-emphasis buttons, this prop is internally overridden to `'high'`.
   * @default ```'low'```
   */
  density?: 'low' | 'medium' | 'high';
  /**
   * Set to `true` to disable the button.
   *
   * NOTE: Ignored when `showSpinner=true`.
   */
  disabled?: boolean;
  /**
   * Set to true to disable the button from being tabbable.
   *
   * NOTE: Setting to true will hide the button from Assistive Technologies (e.g. screen readers). Due to this,
   * we strongly recommended that you do not use this property, unless you know what you're doing.
   */
  disableTabAccessibility?: boolean;
  /**
   * @deprecated We recommend using CSS or TypeStyle instead of this prop.
   *
   * The function that will be called to get content based upon changes of the interactive state.
   * NOTE: If provided, only the contents from this function will be rendered within the button (i.e. any
   * children passed will not be rendered).
   */
  getContent?: (interactionState: InteractionState) => React.ReactChild;
  /**
   * @deprecated instead use `onClick`.
   *
   * The function that will be called when the button is clicked.
   */
  handleClick?: (e: React.SyntheticEvent<HTMLDivElement>) => void;
  /** The overriding role of the button element. Default is 'button'. */
  role?: 'button' | 'radio';
  /**
   * Use this property to set the button's selection state. Default is undefined.
   * NOTE: Either 'aria-pressed' or 'aria-checked' will be set to this value based upon the value of 'role'.
   */
  selected?: boolean;
  /**
   * When `true`:
   * * the button contents are replaced by an activity spinner, e.g. to indicate waiting for a pending server response
   * * the button will not trigger click events
   * * the button will automatically haved `aria-disabled=true`
   * * the button will ignore the `disabled` prop
   */
  showSpinner?: boolean;
  /** The overriding style of the button element. */
  style?: React.CSSProperties;
  /** Test id to be attached to the button. '-Button' will be automatically added as a suffix. */
  testId?: string;
  /** The HTML title attribute of the button. In most cases you should use `Tooltip` as `title` is not keyboard-accessible. */
  title?: string;
}
